import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { fmtDate, fmtRelative } from "../lib/status";
import { REMINDER_TYPE_META, PriorityPill, ReminderStatusBadge } from "../lib/reminders";
import { toast } from "sonner";
import { Bell, RefreshCw, Send, SkipForward, Search, AlertOctagon, CalendarClock, Inbox } from "lucide-react";

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "skipped", label: "Skipped" },
  { value: "failed", label: "Failed" },
];

const PRIORITY_FILTERS = [
  { value: "all", label: "All priorities" },
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

export default function Reminders() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "pending");
  const [priorityFilter, setPriorityFilter] = useState(params.get("priority") || "all");
  const [q, setQ] = useState("");
  const [lastRun, setLastRun] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (priorityFilter !== "all") params.priority = priorityFilter;
      const r = await api.get("/reminders", { params });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter, priorityFilter]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/reminders/run");
      setLastRun({ ...r.data, ranAt: new Date() });
      toast.success(`Reminder run done: ${r.data.created_count} new, ${r.data.sent_count} sent, ${r.data.skipped_count} skipped`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const sendNow = async (id) => {
    try {
      await api.post(`/reminders/${id}/send`);
      toast.success("Reminder sent");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const skipNow = async (id) => {
    const reason = window.prompt("Optional reason for skipping?", "manually_skipped");
    if (reason === null) return;
    try {
      await api.post(`/reminders/${id}/skip`, { reason });
      toast.success("Reminder skipped");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.trim().toLowerCase();
    return items.filter((r) =>
      r.title?.toLowerCase().includes(s)
      || r.message?.toLowerCase().includes(s)
      || r.reminder_type?.toLowerCase().includes(s)
      || r.recipient_email?.toLowerCase().includes(s)
    );
  }, [items, q]);

  const groupCounts = useMemo(() => {
    const out = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const r of items) {
      if (r.status === "pending") out[r.priority] = (out[r.priority] || 0) + 1;
    }
    return out;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="reminders-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Follow-ups</h1>
          <p className="text-slate-600 mt-1">
            Automated nudges for stale confirmations, upcoming pickups, open incidents and more.
            Currently using <span className="font-medium">mock email</span>: runs are manual.
          </p>
        </div>
        <button
          onClick={runNow}
          disabled={running}
          data-testid="reminders-run-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} />
          {running ? "Running…" : "Run reminders now"}
        </button>
      </div>

      {/* Run summary */}
      {lastRun && (
        <div data-testid="reminders-run-summary" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-medium">Last run summary</div>
            {lastRun.ranAt && (
              <div className="text-xs text-emerald-800">
                ran at {lastRun.ranAt.toLocaleTimeString()}
              </div>
            )}
          </div>
          <div className="mt-1">
            Created <b data-testid="run-created">{lastRun.created_count}</b> ·
            Sent <b data-testid="run-sent">{lastRun.sent_count}</b> ·
            Skipped <b data-testid="run-skipped">{lastRun.skipped_count}</b> ·
            Failed <b data-testid="run-failed">{lastRun.failed_count}</b>
          </div>
          {Object.keys(lastRun.counts_by_type || {}).length > 0 && (
            <div className="mt-2 text-xs text-emerald-800 flex flex-wrap gap-2">
              {Object.entries(lastRun.counts_by_type).map(([t, n]) => (
                <span key={t} className="px-2 py-0.5 bg-white border border-emerald-200 rounded-full">
                  {REMINDER_TYPE_META[t]?.short || t}: {n}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Priority overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Critical pending" value={groupCounts.critical} color="from-red-50 to-red-100 text-red-900" testId="rem-stat-critical" icon={AlertOctagon} />
        <Card label="High pending" value={groupCounts.high} color="from-orange-50 to-orange-100 text-orange-900" testId="rem-stat-high" icon={Bell} />
        <Card label="Medium pending" value={groupCounts.medium} color="from-amber-50 to-amber-100 text-amber-900" testId="rem-stat-medium" icon={CalendarClock} />
        <Card label="Low pending" value={groupCounts.low} color="from-slate-50 to-slate-100 text-slate-900" testId="rem-stat-low" icon={Inbox} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="reminders-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, message, recipient…"
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F] text-sm w-80 max-w-full"
          />
        </div>
        <select
          data-testid="reminders-priority-filter"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm"
        >
          {PRIORITY_FILTERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <div className="flex flex-wrap gap-1 ml-auto">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value} data-testid={`rem-filter-${f.value}`}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                statusFilter === f.value
                  ? "bg-[#1B4332] text-white border-[#1B4332]"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading && <div className="p-6 text-slate-500" data-testid="reminders-loading">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center" data-testid="reminders-empty">
            <Bell className="w-8 h-8 mx-auto text-slate-400" />
            <p className="mt-2 text-slate-700 font-medium">No follow-ups due</p>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              ConfirmPH will show follow-ups here when bookings need attention: pending confirmations, pre-pickup checks, and return prep.
            </p>
            <p className="text-xs text-slate-500 mt-2">Try clicking <b>Run reminders now</b> to detect any due.</p>
          </div>
        )}
        <ul className="divide-y divide-slate-100">
          {filtered.map((r) => (
            <li key={r.id} className="p-4 hover:bg-slate-50" data-testid={`reminder-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <PriorityPill priority={r.priority} data-testid={`rem-priority-${r.id}`} />
                    <ReminderStatusBadge status={r.status} data-testid={`rem-status-${r.id}`} />
                    <span className="text-xs text-slate-500">{REMINDER_TYPE_META[r.reminder_type]?.short || r.reminder_type}</span>
                  </div>
                  <div className="font-medium text-slate-900 mt-1">{r.title}</div>
                  <div className="text-sm text-slate-600 mt-1 line-clamp-2">{humanizeIso(r.message)}</div>
                  <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    <span>To: <span className="font-mono">{r.recipient_email || "-"}</span> ({r.recipient_type})</span>
                    <span>Due: {fmtDate(r.due_at)} · {fmtRelative(r.due_at)}</span>
                    {r.sent_at && <span>Sent: {fmtRelative(r.sent_at)}</span>}
                    {r.skip_reason && <span>Skipped: {r.skip_reason}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
                    {r.booking_id && (
                      <Link to={`/app/bookings/${r.booking_id}`} className="text-[#1B4332] hover:underline" data-testid={`rem-link-booking-${r.id}`}>
                        Open booking →
                      </Link>
                    )}
                    {r.incident_id && (
                      <Link to={`/app/incidents/${r.incident_id}`} className="text-[#1B4332] hover:underline" data-testid={`rem-link-incident-${r.id}`}>
                        Open incident →
                      </Link>
                    )}
                    {r.verification_request_id && (
                      <Link to={`/app/requests`} className="text-[#1B4332] hover:underline" data-testid={`rem-link-vrequest-${r.id}`}>
                        Open verification requests →
                      </Link>
                    )}
                  </div>
                </div>
                {r.status === "pending" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => sendNow(r.id)}
                      data-testid={`rem-send-${r.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-xs font-medium"
                    >
                      <Send className="w-3 h-3" /> Send now
                    </button>
                    <button
                      onClick={() => skipNow(r.id)}
                      data-testid={`rem-skip-${r.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-xs font-medium"
                    >
                      <SkipForward className="w-3 h-3" /> Skip
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Card({ label, value, color, icon: Icon, testId }) {
  return (
    <div data-testid={testId} className={`rounded-xl border border-slate-200 bg-gradient-to-br ${color} p-4`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold opacity-70">{label}</div>
        <Icon className="w-4 h-4 opacity-60" />
      </div>
      <div className="mt-2 text-3xl font-display font-semibold">{value}</div>
    </div>
  );
}

// Replace inline ISO timestamps inside a free-text message with a friendly local format.
function humanizeIso(text) {
  if (!text) return text;
  return text.replace(/\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?\b/g,
    (m) => {
      try {
        const d = new Date(m);
        return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
      } catch { return m; }
    });
}
